/* global BigInt */
import React, { useEffect, useState } from 'react';
import { useReadContract } from 'wagmi';
import { LILVERSE_MINT_ADDRESS, LILVERSE_MINT_ABI } from '../../web3/lilverseMintContract';
import { activeChain } from '../../web3/chains';

// Served same-origin by nginx from a cron-refreshed file. It cannot be fetched
// from the browser directly: api.opensea.io returns 401 to any request carrying
// an Origin header, so this has to come via our own server.
const STATS_URL = '/opensea-stats.json';
const STATS_POLL_MS = 120000;

// Past this age the number is more likely to mislead than inform, so it is
// dropped rather than shown stale. Cron refreshes every 10 minutes.
const MAX_STALE_MS = 60 * 60 * 1000;

const MarketStats = () => {
    const [stats, setStats] = useState(null);

    const { data: priceCents } = useReadContract({
        address: LILVERSE_MINT_ADDRESS,
        abi: LILVERSE_MINT_ABI,
        functionName: 'publicPriceCents',
        chainId: activeChain.id,
    });

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const res = await fetch(STATS_URL, { cache: 'no-store' });
                if (!res.ok) throw new Error('stats unavailable');
                const j = await res.json();
                if (cancelled) return;
                const age = Date.now() - new Date(j.updatedAt).getTime();
                setStats(typeof j.floorPrice === 'number' && age < MAX_STALE_MS ? j : null);
            } catch (e) {
                if (!cancelled) setStats(null);
            }
        };
        load();
        const id = setInterval(load, STATS_POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const mintUsd = priceCents !== undefined ? Number(priceCents) / 100 : null;
    const priceLabel = mintUsd === null
        ? null
        : `$${mintUsd % 1 === 0 ? mintUsd.toFixed(0) : mintUsd.toFixed(2)}`;

    // Nothing worth showing yet -- render nothing rather than empty scaffolding.
    if (!priceLabel && !stats) return null;

    return (
        <div className="w-full max-w-[300px] sm:max-w-[430px] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 animate-fade-in-up [animation-delay:300ms]">
            {priceLabel && (
                <span className="font-dang text-[#B8355F] text-base sm:text-xl -tracking-[0.5px] tabular-nums">
                    {priceLabel} per mint
                </span>
            )}

            {priceLabel && stats && (
                <span className="text-[#B8355F]/35 text-sm select-none" aria-hidden="true">•</span>
            )}

            {stats && (
                <a
                    href="https://opensea.io/collection/lilverse"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mons text-[#B8355F]/80 hover:text-[#B8355F] text-xs sm:text-sm tabular-nums no-underline border-b border-[#B8355F]/30 hover:border-[#B8355F] transition-colors"
                >
                    Floor {stats.floorPrice} {stats.floorSymbol} on OpenSea
                </a>
            )}
        </div>
    );
};

export default MarketStats;
